import type { RowData } from "@tanstack/vue-table";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { injectDataCollectionContext } from "./data-collection";
import type { FacetedOptions } from "./faceted-types";

export function useFilterableColumns<TData extends RowData>(
	opts: MaybeRefOrGetter<FacetedOptions<TData>>
) {
	const { manager } = injectDataCollectionContext();

	const allColumns = computed(() => {
		return manager.getHeaderGroups().flatMap((hg) => hg.headers.map((h) => h.column)) || [];
	});

	const filterableColumns = computed(() => {
		return allColumns.value.filter((c) => c.getCanFilter());
	});

	const sortedFilterableColumns = computed(() => {
		const optsVal = toValue(opts);

		const colIdx = new Map<string, number>();
		for (const [idx, col] of Object.keys(optsVal).entries()) {
			colIdx.set(col, idx);
		}

		return [...filterableColumns.value].sort((a, b) => {
			const aIdx = colIdx.get(a.id) ?? Infinity;
			const bIdx = colIdx.get(b.id) ?? Infinity;

			return aIdx - bIdx;
		});
	});

	const activeFiltersCount = computed(() => {
		let count = 0;
		for (const col of filterableColumns.value) {
			count += Number(col.getIsFiltered());
		}
		return count;
	});

	function clearAllFilters() {
		for (const col of filterableColumns.value) {
			col.setFilterValue(undefined);
		}
	}

	return {
		filterableColumns,
		sortedFilterableColumns,
		activeFiltersCount,
		clearAllFilters,
	};
}
